"use client";

import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Checkbox } from "../components/ui/Checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/Table";
import { Filter, Search, Printer, X, AlertTriangle } from "lucide-react";
import { useState, useEffect } from "react";
import { brandService } from "../services/brands";
import { categoryService } from "../services/categories";
import { userService } from "../services/users";
import { productService } from "../services/products";
import { AsyncSelect } from "../components/AsyncSelect";
import { decodeHtmlEntities } from "../utils";
import { toast } from "sonner";
import { Badge } from "../components/ui/Badge";

declare global {
  interface Window {
    zebra_setup: () => void;
    geraMultiplasEtiquetas: (data: any[], print_indices: number[], qualpreco: string) => boolean;
  }
}

interface Product {
  id: string;
  sku: string;
  name: string;
  brand: string;
  category: string;
  price: number;
  original_price: number;
  final_price: number;
  final_avista: number;
  sale_price: number;
}

interface EtiquetasPageProps {
  onNavigate?: (page: string) => void;
}

export function EtiquetasPage({ onNavigate: _onNavigate }: EtiquetasPageProps = {}) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [priceToPrint, setPriceToPrint] = useState<"3" | "2" | "1" | "0">("3"); // 3: Normal, 2: À Vista, 1: Liquidação, 0: Não imprimir
  
  // Guardamos o objeto completo do produto selecionado para evitar erros de undefined
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  
  // Controle de Paginação
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0
  });

  // Controle de Ordenação - Padrão do legado é ordenar pelos últimos atualizados primeiro (updated_at desc)
  const [sortConfig, setSortConfig] = useState({ sort_by: "updated_at", sort_order: "desc" });

  // Controle do Popup de Filtros
  const emptyFilters = {
    marca: "",
    categoria: "",
    fornecedor: "",
    sku: "",
    nome: "",
    rascunho: false,
    disponivel: false,
  };

  const [activeFilters, setActiveFilters] = useState(emptyFilters);
  const [filterLabels, setFilterLabels] = useState({
    marca: "",
    categoria: "",
    fornecedor: ""
  });
  
  const [filterPopup, setFilterPopup] = useState<{
    visible: boolean;
    marca: string;
    categoria: string;
    fornecedor: string;
    sku: string;
    nome: string;
    rascunho: boolean;
    disponivel: boolean;
  }>({
    visible: false,
    ...emptyFilters,
  });

  // Controle do Modal de Pré-visualização / Confirmação
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // Injetar scripts do Zebra BrowserPrint
  useEffect(() => {
    const bp = document.createElement("script");
    bp.src = "/js/print/BrowserPrint-3.0.216.min.js?v=" + Date.now();
    bp.async = true;
    document.head.appendChild(bp);

    bp.onload = () => {
      const zp = document.createElement("script");
      zp.src = "/js/print/zebra-print.js?v=" + Date.now();
      zp.async = true;
      document.head.appendChild(zp);

      zp.onload = () => {
        if (typeof window !== "undefined" && window.zebra_setup) {
          window.zebra_setup();
        }
      };
    };

    return () => {
      // Limpeza opcional
    };
  }, []);

  // Carregar produtos da API
  const fetchProducts = async (page = 1, search = "", filters: any = {}, sort = sortConfig) => {
    try {
      setLoading(true);
      
      const statuses = [];
      if (filters.status) {
        statuses.push(...(Array.isArray(filters.status) ? filters.status : [filters.status]));
      } else {
        // Por padrão no legado, se rascunho/disponivel estiver desmarcado, listamos publicados e rascunhos
        statuses.push("published", "draft");
      }

      const params: any = {
        page,
        search,
        per_page: 15,
        sort_by: sort.sort_by,
        sort_order: sort.sort_order,
        ...filters,
      };

      if (statuses.length > 0) {
        params.status = statuses;
      }

      const response = await productService.getAllProducts(params);
      const { data, meta } = response.data;

      const mappedProducts: Product[] = data.map((p: any) => ({
        id: String(p.id),
        sku: p.sku,
        name: decodeHtmlEntities(p.name) || "Sem Nome",
        brand: decodeHtmlEntities(p.brand?.name) || "Sem Marca",
        category: decodeHtmlEntities(p.categories?.[0]?.name) || "Sem Categoria",
        price: Number(p.price) || 0,
        original_price: Number(p.original_price) || 0,
        final_price: Number(p.final_price) || 0,
        final_avista: Number(p.final_avista) || 0,
        sale_price: Number(p.sale_price) || 0
      }));

      setProducts(mappedProducts);
      setPagination({
        current_page: meta.current_page,
        last_page: meta.last_page,
        total: meta.total
      });
    } catch (error) {
      console.error("Erro ao buscar produtos para etiquetas:", error);
      toast.error("Erro ao carregar produtos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Lógica de Filtros
  const openFilterPopup = () => {
    setFilterPopup({
      visible: true,
      ...activeFilters,
    });
  };

  const closeFilterPopup = () => {
    setFilterPopup({ ...filterPopup, visible: false });
  };

  const buildApiFilters = (f: typeof emptyFilters) => {
    const filters: any = {};
    if (f.marca) filters.brand_id = f.marca;
    if (f.categoria) filters.category_id = f.categoria;
    if (f.sku) filters.sku = f.sku;
    if (f.nome) filters.name = f.nome;
    if (f.fornecedor) filters.user_id = f.fornecedor;

    const statuses = [];
    if (f.rascunho) statuses.push("draft");
    if (f.disponivel) {
      statuses.push("published");
      // Filtro especial implementado no backend para limitar a produtos com estoque e não vendidos
      filters.disponivel = true;
    }

    if (statuses.length > 0) {
      filters.status = statuses;
    }
    return filters;
  };

  const applyFilters = () => {
    const { visible: _, ...filtersOnly } = filterPopup;
    setActiveFilters(filtersOnly);
    fetchProducts(1, searchTerm, buildApiFilters(filtersOnly));
    closeFilterPopup();
  };

  const clearFilters = () => {
    setActiveFilters(emptyFilters);
    setFilterLabels({ marca: "", categoria: "", fornecedor: "" });
    setFilterPopup({ visible: false, ...emptyFilters });
    fetchProducts(1, searchTerm, {});
  };

  const clearSingleFilter = (key: keyof typeof emptyFilters) => {
    const newFilters = { ...activeFilters, [key]: emptyFilters[key] };
    setActiveFilters(newFilters);
    
    if (key === "marca") setFilterLabels(prev => ({ ...prev, marca: "" }));
    if (key === "categoria") setFilterLabels(prev => ({ ...prev, categoria: "" }));
    if (key === "fornecedor") setFilterLabels(prev => ({ ...prev, fornecedor: "" }));

    setFilterPopup(prev => ({ ...prev, [key]: emptyFilters[key] }));
    fetchProducts(1, searchTerm, buildApiFilters(newFilters));
  };

  const getActiveFilterTags = () => {
    const tags: { key: keyof typeof emptyFilters; label: string }[] = [];
    if (activeFilters.marca) tags.push({ key: "marca", label: `Marca: ${filterLabels.marca}` });
    if (activeFilters.categoria) tags.push({ key: "categoria", label: `Categoria: ${filterLabels.categoria}` });
    if (activeFilters.fornecedor) tags.push({ key: "fornecedor", label: `Fornecedor: ${filterLabels.fornecedor}` });
    if (activeFilters.sku) tags.push({ key: "sku", label: `SKU: ${activeFilters.sku}` });
    if (activeFilters.nome) tags.push({ key: "nome", label: `Nome: ${activeFilters.nome}` });
    if (activeFilters.rascunho) tags.push({ key: "rascunho", label: "Apenas Rascunhos" });
    if (activeFilters.disponivel) tags.push({ key: "disponivel", label: "Apenas Disponíveis" });
    return tags;
  };

  // Lógica de Seleção de Produtos (Usando Objeto Completo)
  const toggleProductSelection = (product: Product) => {
    setSelectedProducts(prev =>
      prev.some(p => p.id === product.id)
        ? prev.filter(p => p.id !== product.id)
        : [...prev, product]
    );
  };

  // Lógica de Impressão
  const handlePrintClick = () => {
    if (selectedProducts.length === 0) {
      toast.warning("Selecione pelo menos um produto para imprimir.");
      return;
    }
    setIsConfirmModalOpen(true);
  };

  const triggerLegacyPrint = () => {
    if (typeof window !== "undefined" && window.geraMultiplasEtiquetas) {
      // Mapear produtos para o formato exato que o script legado ZPL do fcadmin espera
      const legacyProducts = selectedProducts.map(p => ({
        brand: {
          label: p.brand
        },
        sku: p.sku,
        sale_price: p.sale_price,
        price_before_sale: p.original_price,
        final_price: p.final_price,
        final_avista: p.final_avista
      }));

      // Como passamos apenas a lista mapeada de produtos selecionados, os índices de impressão
      // na função legada serão exatamente a sequência de 0 até N-1 na mesma ordem selecionada.
      const selectedIndices = selectedProducts.map((_, index) => index);

      window.geraMultiplasEtiquetas(legacyProducts, selectedIndices, priceToPrint);
    } else {
      toast.error("Script de impressão não carregado. Verifique a impressora.");
    }
    setIsConfirmModalOpen(false);
  };

  // Preço a ser impresso na prévia
  const getImpressoPreco = (product: Product) => {
    switch (priceToPrint) {
      case "3": // Preço normal (ou original se houver liquidação)
        return product.sale_price > 0 ? product.original_price : product.final_price;
      case "2": // Preço à vista
        return product.final_avista;
      case "1": // Preço liquidação
        return product.sale_price > 0 ? product.final_price : 0;
      case "0": // Não imprimir
      default:
        return 0;
    }
  };

  return (
    <main className="flex-1 p-6 bg-gray-50">
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <h1 className="text-2xl font-bold text-[#68686A]">Etiquetas</h1>
        <div className="flex items-center gap-2 text-sm text-[#68686A]">
          <span>»</span>
          <span>Listar</span>
        </div>
      </div>

      {/* Barra de Ações (Filtros, Pesquisa, Config de Impressão) */}
      <div className="flex flex-col gap-4 mb-6">
        <Card className="border border-gray-200">
          <CardContent className="p-4 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            
            {/* Controles da Zebra */}
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <label htmlFor="selected_device" className="text-sm font-medium text-[#68686A]">Impressora:</label>
                <select 
                  id="selected_device" 
                  className="w-48 border border-gray-300 rounded-md px-2 py-1 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#D4C070]"
                >
                </select>
              </div>

              <div className="flex items-center gap-2">
                <label htmlFor="tipo_etiqueta" className="text-sm font-medium text-[#68686A]">Tipo de etiqueta:</label>
                <select 
                  id="tipo_etiqueta" 
                  name="etiqueta"
                  className="w-64 border border-gray-300 rounded-md px-2 py-1 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#D4C070]"
                >
                  <option value="4">Etiqueta DUPLA 1,9cm x 4,0cm (Pequena 2)</option>
                  <option value="0">Etiqueta 4,6cm x 3,5cm (Grande)</option>
                  <option value="1">Etiqueta 4,0cm x 2,3cm (Média)</option>
                  <option value="2">Etiqueta 2,0cm x 3,7cm (Pequena)</option>
                  <option value="3">Etiqueta 1,9cm x 4,0cm (Pequena 2)</option>
                </select>
              </div>
            </div>

            {/* Ações primárias */}
            <div className="flex items-center gap-2 w-full md:w-auto">
              <Button
                onClick={handlePrintClick}
                className="bg-[#D4C070] hover:bg-[#849D70] text-white flex-1 md:flex-none"
              >
                <Printer className="h-4 w-4 mr-2" />
                IMPRIMIR
              </Button>
            </div>

          </CardContent>
        </Card>

        {/* Escolha do Preço */}
        <Card className="border border-gray-200">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-[#68686A] mb-3">Qual preço imprimir na etiqueta?</h3>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-[#68686A]">
                <input 
                  type="radio" 
                  name="qualpreco" 
                  value="3" 
                  checked={priceToPrint === "3"} 
                  onChange={() => setPriceToPrint("3")}
                  className="h-4 w-4 text-[#D4C070] focus:ring-[#D4C070]" 
                />
                Preço normal
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-[#68686A]">
                <input 
                  type="radio" 
                  name="qualpreco" 
                  value="2" 
                  checked={priceToPrint === "2"} 
                  onChange={() => setPriceToPrint("2")}
                  className="h-4 w-4 text-[#D4C070] focus:ring-[#D4C070]" 
                />
                Preço à vista
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-[#68686A]">
                <input 
                  type="radio" 
                  name="qualpreco" 
                  value="1" 
                  checked={priceToPrint === "1"} 
                  onChange={() => setPriceToPrint("1")}
                  className="h-4 w-4 text-[#D4C070] focus:ring-[#D4C070]" 
                />
                Preço liquidação
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-[#68686A]">
                <input 
                  type="radio" 
                  name="qualpreco" 
                  value="0" 
                  checked={priceToPrint === "0"} 
                  onChange={() => setPriceToPrint("0")}
                  className="h-4 w-4 text-[#D4C070] focus:ring-[#D4C070]" 
                />
                Não imprimir preço
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Barra de Filtros e Pesquisa de Produtos */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="w-full sm:w-72">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar SKU ou nome..."
                className="w-full pl-9 pr-4 h-10 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4C070] focus:border-transparent bg-white text-gray-700"
                value={searchTerm}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchTerm(val);
                  fetchProducts(1, val, buildApiFilters(activeFilters));
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <select
              className="flex-1 sm:flex-none h-10 px-3 rounded-md border border-gray-300 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#D4C070]"
              value={`${sortConfig.sort_by}|${sortConfig.sort_order}`}
              onChange={(e) => {
                const [sort_by, sort_order] = e.target.value.split('|');
                const newSort = { sort_by, sort_order };
                setSortConfig(newSort);
                fetchProducts(1, searchTerm, buildApiFilters(activeFilters), newSort);
              }}
            >
              <option value="updated_at|desc">Ordenar por Últimos Atualizados</option>
              <option value="created_at|desc">Ordenar por Últimos Cadastrados</option>
              <option value="name|asc">Ordenar por Nome A - Z</option>
              <option value="name|desc">Ordenar por Nome Z - A</option>
              <option value="sku|asc">Ordenar por SKU A - Z</option>
              <option value="sku|desc">Ordenar por SKU Z - A</option>
              <option value="price|asc">Ordenar por Preço (Menor &gt; Maior)</option>
              <option value="price|desc">Ordenar por Preço (Maior &gt; Menor)</option>
            </select>
            
            <Button
              variant="outline"
              onClick={openFilterPopup}
              className="border-gray-300 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070]"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtros
            </Button>

            {getActiveFilterTags().length > 0 && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Limpar filtros
              </Button>
            )}
          </div>
        </div>

        {/* Tags de Filtros Ativos */}
        {getActiveFilterTags().length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="text-sm text-gray-500 mr-1">Filtros ativos:</span>
            {getActiveFilterTags().map(tag => (
              <Badge key={tag.key} variant="secondary" className="bg-[#D4C070]/20 text-gray-800 border-none flex items-center gap-1 font-normal py-1">
                {tag.label}
                <button
                  type="button"
                  onClick={() => clearSingleFilter(tag.key)}
                  className="ml-1 text-gray-500 hover:text-red-500 focus:outline-none"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Tabela de Produtos - Desktop */}
      <Card className="border border-gray-200 hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={products.length > 0 && products.every(p => selectedProducts.some(sp => sp.id === p.id))}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedProducts(prev => {
                        const newSelection = [...prev];
                        products.forEach(p => {
                          if (!newSelection.some(sp => sp.id === p.id)) newSelection.push(p);
                        });
                        return newSelection;
                      });
                    } else {
                      setSelectedProducts(prev => prev.filter(sp => !products.some(p => p.id === sp.id)));
                    }
                  }}
                />
              </TableHead>
              <TableHead className="text-[#68686A]">Produto</TableHead>
              <TableHead className="text-[#68686A]">Preço à vista</TableHead>
              <TableHead className="text-[#68686A]">Preço de Liquidação</TableHead>
              <TableHead className="text-[#68686A]">Preço de Lista</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                  Carregando produtos...
                </TableCell>
              </TableRow>
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                  Nenhum produto encontrado.
                </TableCell>
              </TableRow>
            ) : (
              products.map((product) => (
                <TableRow key={product.id} className="hover:bg-gray-50">
                  <TableCell>
                    <Checkbox
                      checked={selectedProducts.some(sp => sp.id === product.id)}
                      onCheckedChange={() => toggleProductSelection(product)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="font-medium text-gray-900">{product.name}</span>
                      <div className="flex items-center gap-2 text-xs text-[#68686A]">
                        <span>{product.sku}</span>
                        <span>•</span>
                        <span className="font-semibold text-gray-500">{product.brand}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-[#849D70] font-medium">
                      R$ {product.final_avista.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`font-medium ${product.sale_price > 0 ? "text-red-600" : "text-gray-500"}`}>
                      R$ {(product.sale_price > 0 ? product.final_price : 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-gray-900 font-medium">
                      R$ {(product.sale_price > 0 ? product.original_price : product.final_price).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Versão Mobile - Cards */}
      <div className="md:hidden space-y-4">
        {loading ? (
          <div className="text-center py-8 text-gray-500 bg-white rounded-lg border border-gray-200">
            Carregando produtos...
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-white rounded-lg border border-gray-200">
            Nenhum produto encontrado.
          </div>
        ) : (
          products.map((product) => {
            const isSelected = selectedProducts.some(sp => sp.id === product.id);
            return (
              <Card key={product.id} className={`border border-gray-200 ${isSelected ? "border-[#D4C070]/60 bg-[#D4C070]/5" : ""}`}>
                <CardContent className="p-4 space-y-3">
                  
                  {/* Nome e Seleção */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="font-semibold text-gray-900 leading-tight">{product.name}</span>
                      <span className="text-xs text-gray-500 mt-1">
                        {product.sku} • <strong className="text-[#68686A]">{product.brand}</strong>
                      </span>
                    </div>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleProductSelection(product)}
                      className="h-5 w-5 mt-1 shrink-0"
                    />
                  </div>

                  <div className="border-t pt-2 grid grid-cols-3 gap-2 text-center text-xs">
                    {/* Preço à vista */}
                    <div className="flex flex-col gap-0.5 border-r pr-1">
                      <span className="text-[10px] text-gray-500 uppercase tracking-tighter">À Vista</span>
                      <span className="text-[#849D70] font-bold">
                        R$ {product.final_avista.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                      </span>
                    </div>

                    {/* Preço Liquidação */}
                    <div className="flex flex-col gap-0.5 border-r px-1">
                      <span className="text-[10px] text-gray-500 uppercase tracking-tighter">Liquidação</span>
                      <span className={`font-bold ${product.sale_price > 0 ? "text-red-600" : "text-gray-500"}`}>
                        R$ {(product.sale_price > 0 ? product.final_price : 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                      </span>
                    </div>

                    {/* Preço de Lista */}
                    <div className="flex flex-col gap-0.5 pl-1">
                      <span className="text-[10px] text-gray-500 uppercase tracking-tighter">Lista</span>
                      <span className="text-gray-900 font-bold">
                        R$ {(product.sale_price > 0 ? product.original_price : product.final_price).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>

                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Paginação */}
      {!loading && pagination.last_page > 1 && (
        <div className="flex items-center justify-center mt-6">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.current_page === 1}
              onClick={() => fetchProducts(pagination.current_page - 1, searchTerm, buildApiFilters(activeFilters))}
            >
              {"<"}
            </Button>
            {(() => {
              const current = pagination.current_page;
              const last = pagination.last_page;
              const pages: (number | string)[] = [];
              const delta = 2;

              for (let i = 1; i <= last; i++) {
                if (i === 1 || i === last || (i >= current - delta && i <= current + delta)) {
                  pages.push(i);
                } else if (pages[pages.length - 1] !== "...") {
                  pages.push("...");
                }
              }

              return pages.map((page, index) =>
                page === "..." ? (
                  <span key={`dots-${index}`} className="text-gray-400 px-1">...</span>
                ) : (
                  <Button
                    key={page}
                    variant={pagination.current_page === page ? "default" : "outline"}
                    size="sm"
                    className={pagination.current_page === page ? "bg-[#D4C070] hover:bg-[#D4C070] text-white" : "text-[#68686A]"}
                    onClick={() => fetchProducts(page as number, searchTerm, buildApiFilters(activeFilters))}
                  >
                    {page}
                  </Button>
                )
              );
            })()}
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.current_page === pagination.last_page}
              onClick={() => fetchProducts(pagination.current_page + 1, searchTerm, buildApiFilters(activeFilters))}
            >
              {">"}
            </Button>
          </div>
        </div>
      )}

      {/* Popup de Filtros */}
      {filterPopup.visible && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md relative border border-gray-200 shadow-xl max-h-[95vh] overflow-y-auto">
            <button
              onClick={closeFilterPopup}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <X className="h-5 w-5" />
            </button>

            <h3 className="text-lg font-semibold mb-4 text-gray-900">Filtrar Produtos</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                <AsyncSelect
                  value={filterPopup.marca}
                  onChange={(id, lbl) => {
                    setFilterPopup({ ...filterPopup, marca: id || "" });
                    if (id) setFilterLabels(prev => ({ ...prev, marca: lbl }));
                  }}
                  fetcher={async (params) => {
                    const res = await brandService.getAll({ ...params, per_page: 50 });
                    return res.data;
                  }}
                  initialDisplay={filterPopup.marca ? filterLabels.marca : ""}
                  placeholder="Selecione uma marca"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categorias</label>
                <AsyncSelect
                  value={filterPopup.categoria}
                  onChange={(id, lbl) => {
                    setFilterPopup({ ...filterPopup, categoria: id || "" });
                    if (id) setFilterLabels(prev => ({ ...prev, categoria: lbl }));
                  }}
                  fetcher={async (params) => {
                    const res = await categoryService.getAll(params);
                    const items = res.data?.data || res.data || [];
                    return items.reduce((acc: any[], item: any) => {
                      if (item.parent_id > 0 && item.parent) {
                        item.name = `${decodeHtmlEntities(item.parent.name)} > ${decodeHtmlEntities(item.name)}`;
                      } else {
                        item.name = decodeHtmlEntities(item.name);
                      }
                      acc.push(item);
                      return acc;
                    }, []);
                  }}
                  initialDisplay={filterPopup.categoria ? filterLabels.categoria : ""}
                  placeholder="Selecione uma categoria"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fornecedor</label>
                <AsyncSelect
                  value={filterPopup.fornecedor}
                  onChange={(id, lbl) => {
                    setFilterPopup({ ...filterPopup, fornecedor: id || "" });
                    if (id) setFilterLabels(prev => ({ ...prev, fornecedor: lbl }));
                  }}
                  fetcher={async (params) => {
                    if (!params.search || params.search.trim().length < 2) return [];
                    const res = await userService.getAll({ ...params, has_code: 1, sort: "first_name", order: "asc" });
                    return res.data;
                  }}
                  initialDisplay={filterPopup.fornecedor ? filterLabels.fornecedor : ""}
                  placeholder="Selecione um fornecedor"
                  getOptionLabel={(u) => {
                    const fullName = [u.first_name, u.last_name].filter(Boolean).join(" ") || u.name || "";
                    return `[${u.code || u.id}] ${fullName}${u.email ? ` (${u.email})` : ""}`;
                  }}
                  renderOption={(u) => (
                    <div className="flex items-center gap-3 py-1.5 w-full">
                      <div className="flex items-center justify-center min-w-[55px] bg-gray-800 text-white text-[10px] font-bold px-2 py-1 rounded border border-gray-700 uppercase tracking-tighter shadow-sm">
                        {u.code || u.id}
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="font-bold text-gray-900 truncate">
                          {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.name}
                        </span>
                        {u.email && <span className="text-[10px] text-gray-500 truncate font-medium">{u.email}</span>}
                      </div>
                    </div>
                  )}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                <input
                  type="text"
                  value={filterPopup.sku}
                  onChange={(e) => setFilterPopup({ ...filterPopup, sku: e.target.value })}
                  placeholder="SKU"
                  className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent text-sm bg-white text-gray-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={filterPopup.nome}
                  onChange={(e) => setFilterPopup({ ...filterPopup, nome: e.target.value })}
                  placeholder="Nome do produto"
                  className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent text-sm bg-white text-gray-700"
                />
              </div>

              <div className="space-y-2 pt-2">
                <p className="block text-sm font-medium text-gray-700">Status</p>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="rascunho"
                    checked={filterPopup.rascunho}
                    onChange={(e) => setFilterPopup({ ...filterPopup, rascunho: e.target.checked })}
                    className="h-4 w-4 text-[#D4C070] focus:ring-2 focus:ring-[#D4C070] border-gray-300 rounded"
                  />
                  <label htmlFor="rascunho" className="ml-2 text-sm text-gray-700 cursor-pointer">Rascunho</label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="disponivel"
                    checked={filterPopup.disponivel}
                    onChange={(e) => setFilterPopup({ ...filterPopup, disponivel: e.target.checked })}
                    className="h-4 w-4 text-[#D4C070] focus:ring-2 focus:ring-[#D4C070] border-gray-300 rounded"
                  />
                  <label htmlFor="disponivel" className="ml-2 text-sm text-gray-700 cursor-pointer">Disponível</label>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={closeFilterPopup}>
                Cancelar
              </Button>
              <Button className="bg-[#D4C070] hover:bg-[#b09e5c] text-white" onClick={applyFilters}>
                Aplicar Filtros
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Prévia e Confirmação de Impressão */}
      {isConfirmModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg relative border border-gray-200 shadow-xl max-h-[85vh] flex flex-col">
            <button
              onClick={() => setIsConfirmModalOpen(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <X className="h-5 w-5" />
            </button>

            <h3 className="text-lg font-semibold mb-2 text-gray-900 flex items-center gap-2">
              <Printer className="h-5 w-5 text-[#D4C070]" />
              Confirmar Impressão de Etiquetas
            </h3>

            {/* Aviso de desperdício (número ímpar) */}
            {selectedProducts.length % 2 !== 0 && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-3 text-xs mb-4 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <strong>Atenção!</strong> Uma etiqueta será desperdiçada, pois você selecionou uma quantidade ímpar de itens ({selectedProducts.length} itens) para impressão dupla.
                </div>
              </div>
            )}

            <div className="text-sm text-gray-600 mb-4">
              Você selecionou os seguintes produtos para imprimir no formato de preço:{" "}
              <strong className="text-gray-900">
                {priceToPrint === "3" && "Preço Normal"}
                {priceToPrint === "2" && "Preço à Vista"}
                {priceToPrint === "1" && "Preço Liquidação"}
                {priceToPrint === "0" && "Sem Preço"}
              </strong>.
            </div>

            {/* Lista de Pré-visualização baseada em selectedProducts completo */}
            <div className="flex-1 overflow-y-auto border border-gray-200 rounded-md mb-6 divide-y">
              {selectedProducts.map(product => {
                const preco = getImpressoPreco(product);
                return (
                  <div key={product.id} className="p-3 flex items-center justify-between text-sm bg-gray-50 hover:bg-gray-100/70">
                    <div className="flex flex-col min-w-0 pr-4">
                      <span className="font-semibold text-gray-900 truncate">{product.name}</span>
                      <span className="text-xs text-gray-500">{product.sku}</span>
                    </div>
                    <span className="font-bold text-[#849D70] shrink-0">
                      {priceToPrint === "0" ? "Sem Preço" : `R$ ${preco.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-2 shrink-0">
              <Button variant="outline" onClick={() => setIsConfirmModalOpen(false)}>
                Cancelar
              </Button>
              <Button className="bg-[#D4C070] hover:bg-[#849D70] text-white" onClick={triggerLegacyPrint}>
                Confirmar e Imprimir
              </Button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default EtiquetasPage;
